import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { ROUTES } from '../routes';

/**
 * TutorialContext - управление интерактивным туториалом по системе
 * 
 * Предоставляет:
 * - Управление шагами туториала
 * - Навигацию между страницами
 * - Позиционирование подсветки элементов
 * - Состояние (активен/пауза/завершен)
 */

const TutorialContext = createContext(null);

// Шаги туториала для пациента
const PATIENT_TUTORIAL_STEPS = [
    {
        id: 'welcome',
        title: 'Добро пожаловать! 👋',
        content: 'Давайте познакомимся с системой. Этот туториал покажет вам основные возможности платформы.',
        position: 'center',
        route: ROUTES.PROFILE,
    },
    {
        id: 'profile-info',
        title: 'Ваш профиль',
        content: 'Здесь вы можете изменить имя и email. Нажмите "Изменить" рядом с полем, чтобы отредактировать данные.',
        target: '.editable-field:first-child',
        position: 'bottom',
        route: ROUTES.PROFILE,
    },
    {
        id: 'profile-role',
        title: 'Ваша роль',
        content: 'Вы зарегистрированы как пациент. Если у вас есть код доступа врача, вы можете активировать его здесь.',
        target: '.role-section, .info-field:last-child',
        position: 'top',
        route: ROUTES.PROFILE,
    },
    {
        id: 'my-surveys-nav',
        title: 'Назначенные опросы',
        content: 'В этом разделе вы найдёте все опросы, которые назначил ваш врач. Давайте перейдём туда!',
        target: 'a[href="/surveys/my"]',
        position: 'bottom',
        route: ROUTES.PROFILE,
    },
    {
        id: 'surveys-list',
        title: 'Список опросов',
        content: 'Здесь отображаются все ваши активные опросы. Нажмите на карточку опроса, чтобы начать прохождение.',
        target: '.surveys-container, .survey-card, .patient-surveys-content, .page-container',
        position: 'center',
        route: ROUTES.MY_SURVEYS,
    },
    {
        id: 'survey-history-nav',
        title: 'История опросов',
        content: 'Все пройденные опросы сохраняются в истории. Там вы можете посмотреть свои ответы.',
        target: 'a[href="/surveys/history"]',
        position: 'bottom',
        route: ROUTES.MY_SURVEYS,
    },
    {
        id: 'doctors-nav',
        title: 'Ваши врачи',
        content: 'В этом разделе вы можете увидеть список врачей, которые ведут ваше наблюдение.',
        target: 'a[href="/doctors"]',
        position: 'bottom',
        route: ROUTES.MY_SURVEYS,
    },
    {
        id: 'doctors-list',
        title: 'Список врачей',
        content: 'Здесь показаны все врачи, имеющие доступ к вашим данным. Вы можете добавить нового врача по коду.',
        target: '.doctors-container, .doctor-card, .doctors-content, .page-container',
        position: 'center',
        route: ROUTES.DOCTORS,
    },
    {
        id: 'medications-nav',
        title: 'Лекарства',
        content: 'Здесь вы можете отслеживать приём лекарств и устанавливать напоминания.',
        target: 'a[href="/medications/my"]',
        position: 'bottom',
        route: ROUTES.DOCTORS,
    },
    {
        id: 'medications-list',
        title: 'Управление лекарствами',
        content: 'Добавляйте лекарства, устанавливайте расписание приёма и получайте напоминания.',
        target: '.medications-container, .medication-card, .medications-content, .page-container',
        position: 'center',
        route: ROUTES.MY_MEDICATIONS,
    },
    {
        id: 'finish',
        title: 'Туториал завершён! 🎉',
        content: 'Теперь вы знаете основные возможности системы. Если нужна помощь, вы всегда можете вернуться к профилю и запустить туториал снова.',
        position: 'center',
        route: ROUTES.MY_MEDICATIONS,
    },
];

// Шаги туториала для врача
const DOCTOR_TUTORIAL_STEPS = [
    {
        id: 'welcome',
        title: 'Добро пожаловать, доктор! 👨‍⚕️',
        content: 'Давайте познакомимся с возможностями системы для врачей. Этот туториал покажет вам основные инструменты.',
        position: 'center',
        route: ROUTES.PROFILE,
    },
    {
        id: 'profile-info',
        title: 'Ваш профиль',
        content: 'Здесь вы можете изменить имя и email. Нажмите "Изменить" рядом с полем, чтобы отредактировать данные.',
        target: '.editable-field:first-child',
        position: 'bottom',
        route: ROUTES.PROFILE,
    },
    {
        id: 'surveys-nav',
        title: 'Создание опросов',
        content: 'В этом разделе вы можете создавать и редактировать медицинские опросники для пациентов.',
        target: 'a[href="/surveys"]',
        position: 'bottom',
        route: ROUTES.PROFILE,
    },
    {
        id: 'surveys-list',
        title: 'Управление опросами',
        content: 'Здесь отображаются все ваши опросы. Вы можете создать новый или отредактировать существующий.',
        target: '.surveys-container, .survey-card, .surveys-content, .page-container',
        position: 'center',
        route: ROUTES.SURVEYS,
    },
    {
        id: 'questions-nav',
        title: 'База вопросов',
        content: 'Все вопросы хранятся в отдельной базе. Вы можете создавать новые вопросы и использовать их в разных опросах.',
        target: 'a[href="/questions"]',
        position: 'bottom',
        route: ROUTES.SURVEYS,
    },
    {
        id: 'questions-list',
        title: 'Управление вопросами',
        content: 'Создавайте различные типы вопросов: текстовые, с выбором ответа, шкалы и многое другое. Если сделать вопрос публичным, другие врачи смогут использовать его в своих опросах. И вы тоже можете использовать публичные вопросы других докторов!',
        target: '.questions-container, .question-card, .questions-content, .page-container',
        position: 'center',
        route: ROUTES.QUESTIONS,
    },
    {
        id: 'patients-nav',
        title: 'Ваши пациенты',
        content: 'В этом разделе вы управляете списком пациентов и назначаете им опросы.',
        target: 'a[href="/patients"]',
        position: 'bottom',
        route: ROUTES.QUESTIONS,
    },
    {
        id: 'patients-list',
        title: 'Список пациентов',
        content: 'Здесь показаны все ваши пациенты. Вы можете назначить опрос, просмотреть историю ответов или добавить напоминание о приёме лекарств.',
        target: '.patients-container, .patient-card, .patients-content, .page-container',
        position: 'center',
        route: ROUTES.PATIENTS,
    },
    {
        id: 'medications-btn',
        title: 'Напоминания о лекарствах',
        content: 'Нажимая на кнопку "Лекарства" у пациента, вы можете назначить приём лекарств и установить напоминания.',
        target: '.btn-medications, .patient-actions .btn-action:nth-child(2)',
        position: 'top',
        route: ROUTES.PATIENTS,
    },
    {
        id: 'surveys-btn',
        title: 'Назначение опросов',
        content: 'Нажимая на кнопку "Опросы", вы можете назначить пациенту прохождение опроса по расписанию.',
        target: '.btn-schedule, .patient-actions .btn-action:first-child',
        position: 'top',
        route: ROUTES.PATIENTS,
    },
    {
        id: 'finish',
        title: 'Туториал завершён! 🎉',
        content: 'Теперь вы знаете основные возможности системы. Начните с создания опроса или добавления пациентов!',
        position: 'center',
        route: ROUTES.PATIENTS,
    },
];

// Storage key для сохранения состояния туториала
const TUTORIAL_STORAGE_KEY = 'tutorial_state';

// Сохранить состояние в sessionStorage
const saveTutorialState = (isActive, stepIndex, isDoctor) => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify({
            isActive,
            stepIndex,
            isDoctor,
            timestamp: Date.now()
        }));
    } catch (e) {
        console.error('Failed to save tutorial state:', e);
    }
};

// Загрузить состояние из sessionStorage
const loadTutorialState = () => {
    if (typeof window === 'undefined') return null;
    try {
        const saved = sessionStorage.getItem(TUTORIAL_STORAGE_KEY);
        if (saved) {
            const state = JSON.parse(saved);
            // Проверяем, что состояние не устарело (больше 1 часа)
            if (Date.now() - state.timestamp < 3600000) {
                return state;
            }
        }
    } catch (e) {
        console.error('Failed to load tutorial state:', e);
    }
    return null;
};

// Очистить состояние
const clearTutorialState = () => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(TUTORIAL_STORAGE_KEY);
    } catch (e) {
        console.error('Failed to clear tutorial state:', e);
    }
};

export function TutorialProvider({ children }) {
    // Пытаемся восстановить состояние из sessionStorage
    const savedState = loadTutorialState();
    
    const [isActive, setIsActive] = useState(savedState?.isActive || false);
    const [currentStepIndex, setCurrentStepIndex] = useState(savedState?.stepIndex || 0);
    const [steps, setSteps] = useState([]);
    const [targetRect, setTargetRect] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isDoctorMode, setIsDoctorMode] = useState(savedState?.isDoctor || false);
    const [currentRoute, setCurrentRoute] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.location.pathname;
        }
        return '/';
    });
    
    const targetElementRef = useRef(null);

    // Получить текущий шаг
    const currentStep = steps[currentStepIndex] || null;

    // Слушаем изменения маршрута
    useEffect(() => {
        const handlePopState = () => {
            setCurrentRoute(window.location.pathname);
        };
        
        window.addEventListener('popstate', handlePopState);
        
        // Проверяем изменение URL каждые 100ms
        const interval = setInterval(() => {
            if (typeof window !== 'undefined' && window.location.pathname !== currentRoute) {
                setCurrentRoute(window.location.pathname);
            }
        }, 100);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
            clearInterval(interval);
        };
    }, [currentRoute]);

    // Запустить туториал
    const startTutorial = useCallback((isDoctor = false) => {
        if (typeof window === 'undefined') return;
        
        const tutorialSteps = isDoctor ? DOCTOR_TUTORIAL_STEPS : PATIENT_TUTORIAL_STEPS;
        setSteps(tutorialSteps);
        setCurrentStepIndex(0);
        setIsActive(true);
        setIsDoctorMode(isDoctor);
        
        // Сохраняем состояние
        saveTutorialState(true, 0, isDoctor);
        
        // Переходим на первую страницу туториала
        const firstStep = tutorialSteps[0];
        if (firstStep && firstStep.route && window.location.pathname !== firstStep.route) {
            // Принудительно перезагружаем для обновления роутера
            window.location.href = firstStep.route;
        }
    }, []);

    // Остановить туториал
    const stopTutorial = useCallback(() => {
        setIsActive(false);
        setCurrentStepIndex(0);
        setTargetRect(null);
        targetElementRef.current = null;
        clearTutorialState();
    }, []);

    // Перейти к следующему шагу
    const nextStep = useCallback(() => {
        if (currentStepIndex < steps.length - 1) {
            setIsTransitioning(true);
            const nextIndex = currentStepIndex + 1;
            const nextStepData = steps[nextIndex];
            
            // Сохраняем новое состояние перед переходом
            saveTutorialState(true, nextIndex, isDoctorMode);
            
            // Если нужно перейти на другую страницу
            if (typeof window !== 'undefined' && nextStepData.route && window.location.pathname !== nextStepData.route) {
                window.location.href = nextStepData.route;
                return; // Не сбрасываем transitioning, страница перезагрузится
            }
            
            setCurrentStepIndex(nextIndex);
            setTimeout(() => setIsTransitioning(false), 300);
        } else {
            stopTutorial();
        }
    }, [currentStepIndex, steps, stopTutorial, isDoctorMode]);

    // Перейти к предыдущему шагу
    const prevStep = useCallback(() => {
        if (currentStepIndex > 0) {
            setIsTransitioning(true);
            const prevIndex = currentStepIndex - 1;
            const prevStepData = steps[prevIndex];
            
            // Сохраняем новое состояние перед переходом
            saveTutorialState(true, prevIndex, isDoctorMode);
            
            // Если нужно перейти на другую страницу
            if (typeof window !== 'undefined' && prevStepData.route && window.location.pathname !== prevStepData.route) {
                window.location.href = prevStepData.route;
                return; // Не сбрасываем transitioning, страница перезагрузится
            }
            
            setCurrentStepIndex(prevIndex);
            setTimeout(() => setIsTransitioning(false), 300);
        }
    }, [currentStepIndex, steps, isDoctorMode]);

    // Пропустить туториал
    const skipTutorial = useCallback(() => {
        clearTutorialState();
        stopTutorial();
    }, [stopTutorial]);

    // Перейти к конкретному шагу
    const goToStep = useCallback((stepIndex) => {
        if (stepIndex >= 0 && stepIndex < steps.length) {
            setIsTransitioning(true);
            const stepData = steps[stepIndex];
            
            // Сохраняем новое состояние перед переходом
            saveTutorialState(true, stepIndex, isDoctorMode);
            
            if (typeof window !== 'undefined' && stepData.route && window.location.pathname !== stepData.route) {
                window.location.href = stepData.route;
                return; // Не сбрасываем transitioning, страница перезагрузится
            }
            
            setCurrentStepIndex(stepIndex);
            setTimeout(() => setIsTransitioning(false), 300);
        }
    }, [steps, isDoctorMode]);

    // Обновить позицию целевого элемента
    const updateTargetPosition = useCallback(() => {
        if (!isActive || !currentStep || !currentStep.target) {
            setTargetRect(null);
            return;
        }

        // Пробуем найти элемент по селектору
        const selectors = currentStep.target.split(', ');
        let targetElement = null;
        
        for (const selector of selectors) {
            targetElement = document.querySelector(selector.trim());
            if (targetElement) break;
        }

        if (targetElement) {
            targetElementRef.current = targetElement;
            const rect = targetElement.getBoundingClientRect();
            
            // Добавляем небольшой отступ вокруг элемента
            const padding = 8;
            setTargetRect({
                top: rect.top - padding,
                left: rect.left - padding,
                width: rect.width + padding * 2,
                height: rect.height + padding * 2,
            });
        } else {
            targetElementRef.current = null;
            setTargetRect(null);
        }
    }, [isActive, currentStep]);

    // Восстановление steps при загрузке страницы (если туториал был активен)
    useEffect(() => {
        if (isActive && steps.length === 0) {
            // Восстанавливаем steps на основе сохранённого режима
            const restoredSteps = isDoctorMode ? DOCTOR_TUTORIAL_STEPS : PATIENT_TUTORIAL_STEPS;
            setSteps(restoredSteps);
        }
    }, [isActive, steps.length, isDoctorMode]);

    // Отслеживание изменения размера и позиции целевого элемента
    useEffect(() => {
        if (!isActive || !currentStep) return;

        updateTargetPosition();

        // Отслеживаем изменения размера окна
        const handleResize = () => updateTargetPosition();
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleResize, true);
        }

        // Отслеживаем изменения DOM
        const observer = new MutationObserver(() => {
            updateTargetPosition();
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        });

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleResize, true);
            }
            observer.disconnect();
        };
    }, [isActive, currentStep, updateTargetPosition]);

    // Проверка текущего маршрута и корректировка шага
    useEffect(() => {
        if (!isActive || steps.length === 0) return;

        const currentStepData = steps[currentStepIndex];
        if (currentStepData && currentStepData.route && currentRoute !== currentStepData.route) {
            // Найдем подходящий шаг для текущего маршрута
            const matchingStepIndex = steps.findIndex(step => step.route === currentRoute);
            if (matchingStepIndex !== -1 && matchingStepIndex !== currentStepIndex) {
                setCurrentStepIndex(matchingStepIndex);
            }
        }
    }, [currentRoute, isActive, steps, currentStepIndex]);

    const value = {
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps: steps.length,
        targetRect,
        isTransitioning,
        isFirstStep: currentStepIndex === 0,
        isLastStep: currentStepIndex === steps.length - 1,
        startTutorial,
        stopTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        goToStep,
        updateTargetPosition,
    };

    return (
        <TutorialContext.Provider value={value}>
            {children}
        </TutorialContext.Provider>
    );
}

export function useTutorial() {
    const context = useContext(TutorialContext);
    if (!context) {
        throw new Error('useTutorial must be used within a TutorialProvider');
    }
    return context;
}

export default TutorialContext;
